import { cn } from '@rocco/utils/cn';

import {
    fanOutNote,
    selectionSummary,
} from '~/modules/enquiry/bulk/bulk-enquiry-copy';
import { SelectedItemsList } from '~/modules/enquiry/bulk/SelectedItemsList';
import type { BulkEnquiryItem } from '~/modules/enquiry/bulk/bulk-enquiry-state';

interface BulkSelectionPaneProps {
    items: BulkEnquiryItem[];
    supplierCount: number;
    /** Unticking a row removes it from the shared selection. */
    onRemove: (siteTreeID: number) => void;
    /** No controls: the selection is committed, or already sent. */
    readOnly?: boolean;
    /** The enquiries have gone out, so the list is a record rather than a plan. */
    sent?: boolean;
    className?: string;
}

/**
 * The bulk gate's selection list: what is about to be sent, grouped by the
 * professional who will receive it, each row unticked to drop it.
 *
 * Renders in the modal's left pane on desktop (in place of the brand imagery)
 * and inline above the form on mobile, where that pane is hidden — so the list
 * is never the thing a small screen loses.
 */
export const BulkSelectionPane = ({
    items,
    supplierCount,
    onRemove,
    readOnly = false,
    sent = false,
    className,
}: BulkSelectionPaneProps) => {
    const note = fanOutNote(items, supplierCount);

    return (
        <div className={cn('flex size-full flex-col bg-[#f5f5f5]', className)}>
            <div className="px-6 pt-6 pb-3">
                <p className="m-0 text-[1.0625rem] font-medium tracking-[-0.02em] text-[#181818]">
                    {sent ? 'What you sent' : 'Your enquiry list'}
                </p>
                <p className="m-0 mt-1 text-sm text-[#656565]">
                    {selectionSummary(items, supplierCount)}
                </p>
                {/* label-secondary, not muted: #828282 on this pane's #f5f5f5
                    is 3.5:1 and fails AA for body text. */}
                {note ? (
                    <p className="m-0 mt-2 text-[0.8125rem] leading-snug text-[#656565]">
                        {note}
                    </p>
                ) : null}
            </div>
            <SelectedItemsList
                items={items}
                onRemove={onRemove}
                groupBySupplier
                selectable={!readOnly}
                readOnly={readOnly}
                className="min-h-0 flex-1 px-6 pb-6"
                scrollClassName="max-h-none min-h-0 flex-1"
            />
        </div>
    );
};
